import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Language {
  code: string;
  prefix: string;
  name: string;
  nativeName: string;
  flag?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLang = new BehaviorSubject<string>('en');
  
  public readonly availableLanguages: Language[] = [
    { code: 'en', name: 'English', nativeName: 'English',prefix:'EN' },
    { code: 'zh-Hans', name: 'Chinese Simplified', nativeName: '简体中文' ,prefix:'CS' },
    { code: 'zh-Hant', name: 'Chinese Traditional', nativeName: '繁體中文', prefix:'CT' }
  ];

  constructor(private translate: TranslateService) {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    // register languages
    this.translate.addLangs(this.availableLanguages.map(l => l.code));
    // Set default language
    this.translate.setDefaultLang('en');
    
    // Get saved language from localStorage or use browser language
    const savedLang = localStorage.getItem('preferredLanguage');
    const browserLang = this.translate.getBrowserLang();
    
    let languageToUse = 'en';
    
    if (savedLang && this.isLanguageSupported(savedLang)) {
      languageToUse = savedLang;
    } else if (browserLang && this.isLanguageSupported(browserLang)) {
      languageToUse = browserLang;
    }
    
    this.setLanguage(languageToUse);
  }

  setLanguage(lang: string): void {
    if (this.isLanguageSupported(lang)) {
      this.translate.use(lang);
      this.currentLang.next(lang);
      localStorage.setItem('preferredLanguage', lang);
      
      // Update document direction for RTL languages (future use)
      if (lang === 'ar') {
        document.documentElement.setAttribute('dir', 'rtl');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
      }
    }
  }

  getCurrentLang(): Observable<string> {
    return this.currentLang.asObservable();
  }

  getCurrentLangValue(): string {
    return this.currentLang.value;
  }

  isLanguageSupported(lang: string): boolean {
    return this.availableLanguages.some(language => language.code === lang);
  }

  getAvailableLanguages(): Language[] {
    return this.availableLanguages;
  }

  getLanguageByCode(code: string): Language | undefined {
    return this.availableLanguages.find(lang => lang.code === code);
  }

  // Helper method to get current language object
  getCurrentLanguage(): Language | undefined {
    return this.getLanguageByCode(this.currentLang.value);
  }

  // Method to add new language support
  addLanguage(language: Language): void {
    if (!this.availableLanguages.find(lang => lang.code === language.code)) {
      this.availableLanguages.push(language);
      this.translate.addLangs([language.code]);
    }
  }

  // Method to remove language support
  removeLanguage(langCode: string): void {
    const index = this.availableLanguages.findIndex(lang => lang.code === langCode);
    if (index > -1) {
      this.availableLanguages.splice(index, 1);
      
      // If current language is being removed, switch to default
      if (this.currentLang.value === langCode) {
        this.setLanguage('en');
      }
    }
  }
} 